import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Figure from '../Figure';
import Icon from '../Icon';
import Typo from '../Typo';
import SimpleButton from '../molecules/Button/SimpleButton';
import theme from '../theme';
import { toRgba } from '../theme/colors';
import { priceFormater } from '../../helpers/formatNumbers';
import { media } from '../../utils/media';
import Pilltag from '../Pilltag';

const CardWrapper = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  background-color: ${(props) => props.theme.colors.NEU_0};
  box-shadow: ${(props) => props.theme.shadows.BOX_SHADOW_SMALL};
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  width: 100%;

  ${media.lessThan('tablet')} {
    align-items: stretch;
  }

  ${media.lessThan('phablet')} {
    flex-direction: column;
    gap: 0;
  }
`;

const ImageSection = styled.div`
  position: relative;
  width: 180px;
  min-width: 180px;
  flex-shrink: 0;
  align-self: stretch;

  figure {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    &:before {
      display: none;
    }
  }

  ${media.lessThan('tablet')} {
    width: 140px;
    min-width: 140px;
  }

  ${media.lessThan('phablet')} {
    display: none;
  }
`;

const BundleTag = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  background-color: ${({ $bgColor }) => $bgColor};
  padding: 6px 8px;
  border-radius: 8px;
  z-index: 1;
`;

const MobileBundleTag = styled.div`
  display: none;

  ${media.lessThan('phablet')} {
    display: inline-flex;
    align-self: flex-start;
    background-color: ${({ $bgColor }) => $bgColor};
    padding: 6px 8px;
    border-radius: 8px;
  }
`;

const ImagePlaceholder = styled.div`
  background-color: ${(props) => props.theme.colors.NEU_200};
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 1rem 0;
  flex: 1;
  min-width: 0;

  ${media.lessThan('phablet')} {
    padding: 1rem;
  }
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 8px;
  padding: 1rem 1rem 1rem 0;
  flex-shrink: 0;

  ${media.lessThan('phablet')} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-top: 1px solid ${theme.colors.NEU_100};
  }
`;

const CategoriesWrapper = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;

  ${media.lessThan('tablet')} {
    flex-wrap: nowrap;
    overflow: hidden;
  }
`;

const TitleText = styled(Typo.TextTitle)`
  text-overflow: ellipsis;
  white-space: normal;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const CertificateOverlay = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 1;
`;

const IconBackground = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.NEU_50};
  border-radius: 50%;
  padding: 4px;
`;

const PriceGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;

  ${media.lessThan('phablet')} {
    align-items: flex-start;
  }
`;

const MobileCertLabel = styled.div`
  display: none;

  ${media.lessThan('phablet')} {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const ProviderName = styled(Typo.Info)`
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CourseListCard = ({ courseInfo, onSelect, onCardClick, hideVAT, isSelected }) => {
  const { t } = useTranslation();
  const { coverImage, coverUrl, name, price, currency, categories, ownerName } = courseInfo;
  const imageUrl = !coverImage ? coverUrl : Array.isArray(coverImage) ? coverImage[0]?.url : coverImage?.url;

  const isPackage = courseInfo?.type === 2;
  const packageSize = courseInfo?.courses?.length;
  const hasCertificate = courseInfo?.hasCertificate;

  const handleSelect = (e) => {
    e.stopPropagation();
    if (onSelect) onSelect(courseInfo);
  };

  const handleCardClick = () => {
    if (onCardClick) onCardClick(courseInfo);
  };

  return (
    <CardWrapper onClick={handleCardClick}>
      <ImageSection>
        {isPackage && packageSize && (
          <BundleTag $bgColor={toRgba(theme.colors.NEU_999, 0.65)}>
            <Typo.Info $themeColor={theme.colors.NEU_999}>{t('webshop.bundle', { count: packageSize })}</Typo.Info>
          </BundleTag>
        )}
        {hasCertificate && (
          <CertificateOverlay>
            <IconBackground>
              <Icon.Certificate size='x-small' color={theme.colors.SECONDARY} />
            </IconBackground>
          </CertificateOverlay>
        )}
        <Figure.Widescreen>{!imageUrl ? <ImagePlaceholder /> : <img src={imageUrl} alt={name} loading='lazy' />}</Figure.Widescreen>
      </ImageSection>

      <InfoSection>
        {isPackage && packageSize && (
          <MobileBundleTag $bgColor={toRgba(theme.colors.NEU_999, 0.65)}>
            <Typo.Info $themeColor={theme.colors.NEU_999}>{t('webshop.bundle', { count: packageSize })}</Typo.Info>
          </MobileBundleTag>
        )}
        {ownerName && <ProviderName color={theme.colors.NEU_600}>{ownerName}</ProviderName>}
        <TitleText>{name}</TitleText>
        {categories?.length > 0 && (
          <CategoriesWrapper>
            {categories.map((cat, i) => (
              <Pilltag key={i} label={cat.name || cat} fontSize={theme.fontSizes.xSmall} />
            ))}
          </CategoriesWrapper>
        )}
      </InfoSection>

      <RightSection>
        <PriceGroup>
          {hasCertificate && (
            <MobileCertLabel>
              <Icon.Certificate size='x-small' color={theme.colors.SECONDARY} />
              <Typo.Info color={theme.colors.SECONDARY} fontWeight='600'>
                {t('webshop.certificate')}
              </Typo.Info>
            </MobileCertLabel>
          )}
          <Typo.TextTitle>{priceFormater(price, hideVAT, currency)}</Typo.TextTitle>
        </PriceGroup>
        {onSelect && (
          <SimpleButton
            variant={isSelected ? 'tertiary' : 'secondary'}
            size='small'
            bgColor={isSelected ? theme.colors.ACTION : undefined}
            onClick={handleSelect}
          >
            {isSelected ? t('globals.remove', 'Remove') : t('webshop.select')}
          </SimpleButton>
        )}
      </RightSection>
    </CardWrapper>
  );
};

export default CourseListCard;
